import { Routes, Route, useLocation } from 'react-router-dom';
import { Suspense, lazy, useEffect } from 'react';
import { Layout } from './components/Layout';
import { UIProvider } from './contexts/UIContext';
import { HomePage } from './pages/HomePage';
import ComparePage from './pages/ComparePage';
import { Reviews } from './pages/Reviews';
import { BlogPage } from './pages/BlogPage';
import { TestimonialsPage } from './pages/TestimonialsPage';
import { AboutPage } from './pages/AboutPage';
import { AffiliateRedirect } from './components/AffiliateRedirect';
import { LoginPage } from './pages/LoginPage';
import { PasswordResetPage } from './pages/PasswordResetPage';
import { PasswordResetConfirmPage } from './pages/PasswordResetConfirmPage';
import { NewsletterDashboard } from './pages/NewsletterDashboard';
import { useScrollToTop } from './hooks/useScrollToTop';
import { CategoryPage } from './pages/CategoryPage';
import { TermsOfUsePage } from './pages/TermsOfUsePage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { SubscribePage } from './pages/SubscribePage';
import { ThankYouPage } from './pages/ThankYouPage';
import { NotFoundPage } from './pages/NotFoundPage';
import LotCalculatorPage from './pages/tools/LotCalculatorPage';
import { NewsletterPopup } from './components/NewsletterPopup';
import { trackPageView } from './utils/analytics';

// Lazy load heavy pages
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const ReviewDetailPage = lazy(() => import('./pages/ReviewDetailPage'));
const BestPropFirmsPage = lazy(() => import('./pages/BestPropFirmsPage'));

// Loading component for lazy-loaded pages
const LoadingFallback = () => (
  <div className="min-h-screen bg-gray-950 text-yellow-100 flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-400 mx-auto mb-4"></div>
      <p className="text-yellow-400">Loading...</p>
    </div>
  </div>
);

export function App() {
  useScrollToTop();
  const location = useLocation();

  // Track page views
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);

  return (
    <UIProvider>
      {location.pathname === '/' && <NewsletterPopup />}

      <Routes>
        <Route path="/" element={<Layout />}>
          <Route index element={<HomePage />} />
          <Route path="compare" element={<ComparePage />} />
          <Route path="reviews" element={<Reviews />} />
          <Route 
            path="reviews/:firmSlug" 
            element={
              <Suspense fallback={<LoadingFallback />}>
                <ReviewDetailPage />
              </Suspense>
            } 
          />
          <Route path="blog" element={<BlogPage />} />
          <Route 
            path="blog/:slug" 
            element={
              <Suspense fallback={<LoadingFallback />}>
                <BlogPostPage />
              </Suspense>
            } 
          />
          <Route path="testimonials" element={<TestimonialsPage />} />
          <Route path="about" element={<AboutPage />} />
          <Route path="go/:firmId" element={<AffiliateRedirect />} />
          <Route path="login" element={<LoginPage />} />
          <Route path="reset-password" element={<PasswordResetPage />} />
          <Route path="reset-password/confirm" element={<PasswordResetConfirmPage />} />
          <Route path="newsletter-dashboard" element={<NewsletterDashboard />} />
          <Route path="blog/category/:slug" element={<CategoryPage />} />
          <Route path="terms-of-use" element={<TermsOfUsePage />} />
          <Route path="privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="subscribe" element={<SubscribePage />} />
          <Route path="thank-you" element={<ThankYouPage />} />
          <Route
            path="best-prop-firms"
            element={
              <Suspense fallback={<LoadingFallback />}>
                <BestPropFirmsPage />
              </Suspense>
            }
          />
          <Route path="*" element={<NotFoundPage />} />
          <Route path="tools/lot-calculator" element={<LotCalculatorPage />} />
        </Route>
      </Routes>
    </UIProvider>
  );
}

export default App;
